import {
    useCallback, useEffect, useRef, useState,
} from 'react';

import { PdfViewerClient } from '../client/pdfViewerClient';
import {
    SEARCH_BATCH, SEARCH_LIMIT, SEARCH_MAX_RESULTS, SEARCH_MIN_CHARS,
} from '../constants';
import { SearchPage, SearchResult } from '../types';

export interface SearchState {
    results: SearchResult[];
    active: number;
    isRunning: boolean;
    hasFailed: boolean;
}

const INITIAL: SearchState = {
    results: [],
    active: -1,
    isRunning: false,
    hasFailed: false,
};

interface UsePdfSearchResult {
    state: SearchState;
    jump: number;
    run: (query: string) => Promise<void>;
    reset: () => void;
    step: (direction: 1 | -1) => void;
}

const usePdfSearch = (client: PdfViewerClient): UsePdfSearchResult => {
    const [state, setState] = useState<SearchState>(INITIAL);
    const [jump, setJump] = useState<number>(0);
    const tokenRef = useRef<number>(0);
    const controllerRef = useRef<AbortController | null>(null);

    const cancelRun = useCallback(() => {
        tokenRef.current += 1;
        controllerRef.current?.abort();
        controllerRef.current = null;
    }, []);

    useEffect(() => cancelRun, [cancelRun]);

    const reset = useCallback(() => {
        cancelRun();
        setState(INITIAL);
    }, []);

    const run = useCallback(async (query: string): Promise<void> => {
        cancelRun();
        const token = tokenRef.current;
        if (query.length < SEARCH_MIN_CHARS) {
            setState(INITIAL);
            return;
        }
        const controller = new AbortController();
        controllerRef.current = controller;
        setState({ ...INITIAL, isRunning: true });

        const loadFrom = async (start: number, collected: SearchResult[]): Promise<void> => {
            let data: SearchPage;
            try {
                data = await client.search(query, start, SEARCH_BATCH, SEARCH_LIMIT, controller.signal);
            } catch (error) {
                if (token === tokenRef.current) {
                    setState((prev) => ({ ...prev, isRunning: false, hasFailed: true }));
                }
                return;
            }
            if (token !== tokenRef.current) {
                return;
            }
            const results = [...collected, ...data.results];
            setState((prev) => ({
                ...prev,
                results,
                active: prev.active < 0 && results.length ? 0 : prev.active,
            }));
            if (!collected.length && results.length) {
                setJump((value) => value + 1);
            }
            if (data.next !== null && results.length < SEARCH_MAX_RESULTS) {
                await loadFrom(data.next, results);
            } else {
                setState((prev) => ({ ...prev, isRunning: false }));
            }
        };

        await loadFrom(0, []);
    }, [client, cancelRun]);

    const step = useCallback((direction: 1 | -1) => {
        setState((prev) => {
            const count = prev.results.length;
            return count ? { ...prev, active: (prev.active + direction + count) % count } : prev;
        });
        setJump((value) => value + 1);
    }, []);

    return {
        state, jump, run, reset, step,
    };
};

export default usePdfSearch;
